import React, { useState, useEffect } from "react";
import { Button, CustomSelect, TextInput } from "..";
import { UserModel } from "@/database";
import Avatar from "../../assets/icons/img/avatar/avatar.png";
import Modal from "../Modal/Modal";
import { useLocation } from "react-router-dom";
import { userStatusToTextAndColor } from "../../utils";
import { SERVICES } from "@/utils/constants/uiConstants";
import {
  checkCMSPermission,
  getPermissionId,
  transformPermissionForDisplay,
  getBasePermission,
  filterPermissionsBySearch,
  generatePermissionOptions,
  updatePermissionStatus,
  getUserServiceByAdminType,
  ADMIN_TYPE_LEVELS,
} from "@/utils/permission";

interface UserServicesProps {
  value: string[];
  onChange: (updated: string[]) => void;
  productList: UserModel[];
  businessList: UserModel[];
  onRequest?: (e: any) => void;
  isNewAdd?: boolean;
  isReject?: boolean;
  isProfile?: boolean;
  currentUser?: any;
  ServiceRequest?: any;
  isRequested?: boolean;
  status?: string;
  requestedPermissionsStatus?: any[];
  showHeader?: boolean;
  showPermissionCount?: number;
  showRequired?: boolean;
  touched?: boolean;
  error?: string;
}

const UserServices: React.FC<UserServicesProps> = ({
  value,
  onChange,
  productList,
  businessList,
  isNewAdd = true,
  isProfile = false,
  currentUser,
  ServiceRequest,
  isRequested = false,
  requestedPermissionsStatus,
  showHeader = true,
  showPermissionCount = 4,
  showRequired = true,
  touched = false,
  error = "",
}) => {
  const [permissionOptions, setPermissionOptions] = useState<any[]>([]);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [modalPermissions, setModalPermissions] = useState<string[]>(value);
  const [showAll, setShowAll] = useState(false);
  const [serviceName, setServiceName] = useState("");
  const location = useLocation();
  const [newModalPermissionOptions, setNewModalPermissionOptions] = useState<
    any[]
  >([]);
  const [addedPermissions, setAddedPermissions] = useState<any[]>(value || []);
  const [requestedPermissions, setRequestedPermissions] = useState<any[]>([]);
  const [searchPermissionList, setSearchPermissionList] = useState<any[]>([]);
  const [originalPermissionList, setOriginalPermissionList] = useState<any[]>(
    []
  );

  useEffect(() => {
    const transformedPermissions = value?.map((permission) =>
      transformPermissionForDisplay(permission)
    );
    setAddedPermissions(transformedPermissions);
  }, [value]);

  useEffect(() => {
    const updated = currentUser?.user_req?.map((req: any) => {
      return req.business + "." + req.team + "." + req.service;
    });
    setRequestedPermissions(updated);
  }, [currentUser?.user_req]);

  useEffect(() => {
    const options = generatePermissionOptions(
      SERVICES,
      businessList,
      productList
    );
    setPermissionOptions(options);
  }, [businessList, productList]);

  const handleServiceNameChange = (val: string) => {
    setServiceName(val);
    const filteredOptions = filterPermissionsBySearch(
      val,
      originalPermissionList,
      businessIdToNameMapping,
      productIdToNameMapping
    );
    setNewModalPermissionOptions([...filteredOptions]);
  };

  const sampleListings = value?.map((permission) => {
    const [businessId, productId, service, permissionType = "r"] =
      permission?.split(".");

    return {
      id: permission,
      title: `${businessId}.${productId}.${service}`,
      isChecked: true,
      image: Avatar,
      permission: permissionType,
    };
  });

  const handleStatusChange = (val: string, id: string) => {
    const updated = value.map((permission) => {
      if (permission === id) {
        const parts = permission.split(".");
        parts[parts.length - 1] = val;
        return parts.join(".");
      }
      return permission;
    });
    onChange(updated);
  };

  const handleDoneModal = () => {
    setIsNewModalOpen(false);
  };

  const businessIdToNameMapping = businessList?.reduce(
    (acc: any, business: any) => {
      acc[business.id] = business.name;
      return acc;
    },
    {} as Record<string, string>
  );

  const productIdToNameMapping = productList?.reduce(
    (acc: any, product: any) => {
      acc[product.id] = product.name;
      return acc;
    },
    {} as Record<string, string>
  );

  const handleOpenNewModal = (e: any) => {
    e.preventDefault();
    setModalPermissions([]);
    setIsNewModalOpen(true);
  };

  const handleCancelModal = () => {
    setIsNewModalOpen(false);
  };

  const modalPermissionOptions = permissionOptions.filter((opt) => {
    return !value.some((perm) => getBasePermission(perm) === opt.value);
  });

  useEffect(() => {
    const updatedPermissions = permissionOptions.map((permission) => {
      const id = getPermissionId(permission.value);
      let updatedId = id;
      addedPermissions.forEach((perm) => {
        const newId = getPermissionId(perm);
        if (newId === id) {
          updatedId = perm;
        }
      });
      permission.value = updatedId;
      return permission;
    });

    const updatePermissionsForRequest = updatedPermissions.filter(
      (permission) => {
        const id = getPermissionId(permission.value);
        let isPresent = false;
        requestedPermissions?.forEach((req) => {
          const reqId = getPermissionId(req);
          if (reqId === id) {
            isPresent = true;
          }
        });
        if (isPresent) {
          return false;
        }
        return true;
      }
    );

    const updated = updatePermissionsForRequest.map((option) => {
      if (option.value.split(".").length === 3) {
        return {
          ...option,
          value: option.value + ".r",
        };
      }
      return option;
    });

    setNewModalPermissionOptions(updated);
    setSearchPermissionList(updated);
    setOriginalPermissionList(updated);
  }, [permissionOptions, requestedPermissions]);

  const handleChangePermission = (newValue: any, id: string) => {
    const state = {
      addedPermissions,
      setAddedPermissions,
      onChange,
      newModalPermissionOptions,
      setNewModalPermissionOptions,
      setSearchPermissionList,
      originalPermissionList,
      setOriginalPermissionList,
    };
    updatePermissionStatus(id, newValue, state);
  };

  const handleAddService = (permission: any) => {
    const per = permission.value;
    const updated = [...addedPermissions, per];
    setAddedPermissions(updated);
    onChange(updated);
  };

  const handleRemoveFromList = (id: any) => {
    const updated = addedPermissions.filter((per) => per !== id);
    setAddedPermissions(updated);
    onChange(updated);
  };

  const handleRemoveService = (permission: any) => {
    const updated = addedPermissions.filter((per) => per !== permission.value);
    setAddedPermissions(updated);
    onChange(updated);
  };

  const handleRequestService = async (permission: any) => {
    const businessId = permission.value.split(".")[0];
    const productId = permission.value.split(".")[1];
    const serviceId =
      (permission.value.split(".")[2] === "All"
        ? "*"
        : permission.value.split(".")[2]) +
      "." +
      permission.value.split(".")[3];
    const service = permission.value.split(".")[2];

    const payload = {
      business: businessId === "All Business" ? "*" : businessId,
      team: productId === "All Team" ? "*" : productId,
      service: serviceId,
      id: currentUser?.id,
    };

    await ServiceRequest(payload);
    const updated = newModalPermissionOptions.filter((per) => {
      const parts = per.value.split(".");
      if (
        parts[0] === businessId &&
        parts[1] === productId &&
        parts[2] === service
      ) {
        return false;
      }
      return true;
    });
    setNewModalPermissionOptions(updated);
    // Also update searchPermissionList to keep it in sync
    const updatedSearchList = searchPermissionList.filter((per) => {
      const parts = per.value.split(".");
      if (
        parts[0] === businessId &&
        parts[1] === productId &&
        parts[2] === service
      ) {
        return false;
      }
      return true;
    });
    setSearchPermissionList(updatedSearchList);
    // Also update originalPermissionList to keep it in sync
    const updatedOriginalList = originalPermissionList.filter((per) => {
      const parts = per.value.split(".");
      if (
        parts[0] === businessId &&
        parts[1] === productId &&
        parts[2] === service
      ) {
        return false;
      }
      return true;
    });
    setOriginalPermissionList(updatedOriginalList);
  };

  const visibleListings = showAll
    ? sampleListings
    : sampleListings?.slice(0, showPermissionCount);

  return (
    <>
      <div className="p-0">
        {showHeader && (
          <div className="card-header d-flex align-items-center justify-content-between">
            <div>
              <h6 className="me-2 d-flex align-items-center gap-1 mb-0">
                {isRequested ? "Requested Services" : "Manage Access"}
                {showRequired && <span className="required">*</span>}
              </h6>
              {touched && error && (
                <div className="text-danger small mt-1">{error}</div>
              )}
            </div>
            <Button
              type="outline-primary"
              isDisabled={
                getUserServiceByAdminType(
                  currentUser?.permissions,
                  ADMIN_TYPE_LEVELS.super
                )?.access === "global access" && isProfile
              }
              onClick={handleOpenNewModal}
              className="btn d-flex"
            >
              Add Access
            </Button>
          </div>
        )}
        {isNewModalOpen && (
          <Modal
            title="Add Access"
            isOpen={isNewModalOpen}
            onClose={handleCancelModal}
            width="900px"
            height="800px"
            footer={
              <div className="d-flex justify-content-end">
                <button
                  disabled={addedPermissions.length === 0}
                  className="btn btn-primary"
                  onClick={handleDoneModal}
                >
                  Done
                </button>
              </div>
            }
          >
            <div className="row">
              <div
                className="bg-light border-bottom mb-2 px-3 py-2 pb-3 position-sticky z-9"
                style={{ top: "-16px" }}
              >
                <TextInput
                  label="Search Service"
                  name="serviceName"
                  placeholder="Search Service"
                  value={serviceName}
                  onChange={(e) => handleServiceNameChange(e.target.value)}
                />
              </div>
              <div className="row mt-3">
                {newModalPermissionOptions?.map((permission, ind) => {
                  const [businessId, productId, service] =
                    permission.label.split("⸬");
                  const arrayofpermisison = permission.value.split(".");

                  const businessName =
                    businessIdToNameMapping[businessId] ||
                    (businessId == "*" ? "All Business" : businessId);
                  const productName =
                    productIdToNameMapping[productId] || productId;
                  let permissionType = "";
                  if (arrayofpermisison.length > 3) {
                    permissionType =
                      arrayofpermisison[arrayofpermisison.length - 1];
                  } else {
                    permissionType = "r";
                  }

                  return (
                    <div
                      key={ind}
                      className="col-md-6 mb-3"
                      style={{ position: "relative" }}
                    >
                      <div className="card catRow d-flex flex-row p-3">
                        <div className="">
                          <div className="d-flex flex-column">
                            <div className="fw-medium pb-2">
                              {businessName} - {productName} - {service}
                            </div>
                            <span className="d-flex align-items-center gap-2 text-secondary">
                              Access:
                              <CustomSelect
                                name={`modal-permission-${permission}`}
                                value={permissionType}
                                maintainSelectHeight="20px"
                                options={[
                                  { label: "Read Only", value: "r" },
                                  { label: "Read Write", value: "w" },
                                  { label: "Admin", value: "*" },
                                ]}
                                className="m-0 p-0"
                                handleChange={(newValue: any) =>
                                  handleChangePermission(
                                    newValue.value,
                                    permission.value
                                  )
                                }
                              />
                            </span>
                          </div>
                        </div>
                        <div className="ms-auto">
                          {addedPermissions.includes(permission.value) ? (
                            <button
                              className={`btn btn-sm btn-outline-primary active ${
                                isProfile
                                  ? "btn-outline-success"
                                  : "btn-outline-secondary"
                              }`}
                              onClick={() => {
                                if (isProfile) {
                                  handleRequestService(permission);
                                } else {
                                  handleRemoveService(permission);
                                }
                              }}
                            >
                              {isProfile ? "Upgrade" : "Remove"}
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                if (isProfile) {
                                  handleRequestService(permission);
                                } else {
                                  handleAddService(permission);
                                }
                              }}
                            >
                              {isProfile ? "Request" : "Add"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Modal>
        )}

        {showHeader && (
          <div className="rowTh row px-3 py-2 bg-light m-0">
            <div className="col-md-8 fw-bold text-uppercase">Access</div>
            <div className="col-md-4 fw-bold text-uppercase">Access Level</div>
          </div>
        )}
        {visibleListings.map((item: any, index?: number) => {
          const [businessId, productId, service] = item.title.split(".");
          const businessName =
            businessIdToNameMapping[businessId] ||
            (businessId == "*" ? "All Business" : businessId);
          const productName =
            productIdToNameMapping[productId] ||
            (productId == "*" ? "All Team" : productId);
          const serviceName = service == "*" ? "All" : service;
          return (
            <div
              key={item.id}
              className="row px-3 py-2 border-bottom m-0 "
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{ position: "relative" }}
            >
              {/* <img src={item.image} alt={item.title} className="me-2" style={{ width: '50px', height: '50px' }} /> */}
              <div className="col-md-8 listing-title align-content-center">
                {businessName} - {productName} - {serviceName}
              </div>
              <div className="col-md-4 d-flex align-middle gap-2">
                <CustomSelect
                  name={`permission-${item.id}`}
                  maintainSelectHeight="20px"
                  value={item.permission}
                  options={[
                    { label: "Read Only", value: "r" },
                    { label: "Read Write", value: "w" },
                    { label: "Admin", value: "*" },
                  ]}
                  handleChange={(newValue: any) => {
                    if (isNewAdd) {
                      handleChangePermission(newValue.value, item.id);
                    } else {
                      handleStatusChange(newValue.value, item.id);
                    }
                  }}
                  disabled={isProfile}
                />
                {isRequested && (
                  <div className="d-flex align-items-center">
                    <span className="badge text-bg-secondary">
                      {userStatusToTextAndColor(
                        requestedPermissionsStatus?.[index || 0] || 0
                      )}
                    </span>
                  </div>
                )}
                {isNewAdd && isProfile === false && (
                  <div
                    className="d-flex align-items-center"
                    onClick={() => handleRemoveFromList(item.id)}
                    style={{ height: "30px", margin: "auto 0" }}
                  >
                    <i className="bi bi-x-circle iconSvg"></i>
                  </div>
                )}
                <div
                  className="btn btn-sm H"
                  style={{ height: "30px", margin: "auto 0" }}
                >
                  Update
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {sampleListings.length > showPermissionCount && (
        <div className="d-flex justify-content-center mt-2">
          <div
            className="loadMore d-flex align-items-center z-1"
            onClick={() => setShowAll((prev) => !prev)}
            style={{ cursor: "pointer" }}
          >
            {showAll ? "Show Less" : "Load More"}
          </div>
        </div>
      )}
    </>
  );
};

export default UserServices;
